import { z } from 'zod'
import { apiRequest, apiRequestMultipart } from '@/lib/api-client'

export const contextDocumentSchema = z.object({
  id: z.string(),
  workspace_id: z.string(),
  title: z.string(),
  description: z.string().nullable(),
  document_type: z.enum(['company', 'user', 'project', 'general']),
  extracted_text: z.string(),
  storage_path: z.string().nullable(),
  file_mime: z.string().nullable(),
  is_active: z.boolean(),
  created_at: z.string(),
  updated_at: z.string(),
})
export type ContextDocument = z.infer<typeof contextDocumentSchema>

const documentsListSchema = z.object({ documents: z.array(contextDocumentSchema) })

export interface ContextCreateInput {
  title: string
  description?: string
  document_type?: 'company' | 'user' | 'project' | 'general'
  extracted_text: string
}

export interface ContextUpdateInput {
  title?: string
  description?: string
  document_type?: 'company' | 'user' | 'project' | 'general'
  extracted_text?: string
  is_active?: boolean
}

export async function listContextDocuments(): Promise<ContextDocument[]> {
  const result = await apiRequest({
    path: '/context/documents',
    schema: documentsListSchema,
  })
  return result.documents
}

export async function createContextDocument(input: ContextCreateInput): Promise<ContextDocument> {
  return await apiRequest({
    method: 'POST',
    path: '/context/documents',
    body: input,
    schema: contextDocumentSchema,
  })
}

export async function updateContextDocument(id: string, input: ContextUpdateInput): Promise<ContextDocument> {
  return await apiRequest({
    method: 'PATCH',
    path: `/context/documents/${id}`,
    body: input,
    schema: contextDocumentSchema,
  })
}

export async function deleteContextDocument(id: string): Promise<void> {
  await apiRequest({
    method: 'DELETE',
    path: `/context/documents/${id}`,
    schema: z.object({ success: z.boolean() }),
  })
}

export async function uploadContextDocument(
  file: File,
  options?: { title?: string; document_type?: 'company' | 'user' | 'project' | 'general' },
): Promise<ContextDocument> {
  const formData = new FormData()
  formData.append('file', file)
  if (options?.title) formData.append('title', options.title)
  if (options?.document_type) formData.append('document_type', options.document_type)
  return await apiRequestMultipart({
    path: '/context/documents/upload',
    formData,
    schema: contextDocumentSchema,
  })
}
